// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface DraftBoard {
  id: string;
  week_start: string;
  week_end: string;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
}

export interface DraftPick {
  id: string;
  draft_board_id: string;
  post_id: string;
  pick_number: number;
  total_reactions: number;
  reaction_variety: number;
  created_at: string;
  post?: {
    id: string;
    content: string | null;
    media_urls: string[] | null;
    reactions_count: Record<string, number>;
    created_at: string;
    profiles: {
      id: string;
      name: string | null;
      avatar_url: string | null;
      username: string | null;
    };
  };
}

export const useDraftBoard = (weekStart?: string) => {
  const queryClient = useQueryClient();

  // Fetch the latest published draft board
  const { data: currentBoard, isLoading: boardLoading } = useQuery({
    queryKey: ['draft_board', 'current'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wall_draft_boards')
        .select('*')
        .eq('is_published', true)
        .order('week_start', { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching draft board:', error);
        return null;
      }

      return data as DraftBoard | null;
    },
  });

  // Fetch picks for a specific board
  const { data: picks, isLoading: picksLoading } = useQuery({
    queryKey: ['draft_picks', currentBoard?.id],
    queryFn: async () => {
      if (!currentBoard?.id) return [];

      const { data, error } = await supabase
        .from('wall_draft_picks')
        .select(`
          *,
          post:wall_posts (
            id,
            content,
            media_urls,
            reactions_count,
            created_at,
            profiles:profile_id (
              id,
              name,
              avatar_url,
              username
            )
          )
        `)
        .eq('draft_board_id', currentBoard.id)
        .order('pick_number', { ascending: true });

      if (error) {
        console.error('Error fetching draft picks:', error);
        return [];
      }

      return (data || []) as DraftPick[];
    },
    enabled: !!currentBoard?.id,
  });

  // Admin: Generate draft board
  const generateMutation = useMutation({
    mutationFn: async (weekStart?: string) => {
      const { data, error } = await supabase.rpc('generate_weekly_draft_board', {
        p_week_start: weekStart || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Draft board generated with ${data.picks_added} picks!`);
      queryClient.invalidateQueries({ queryKey: ['draft_board'] });
      queryClient.invalidateQueries({ queryKey: ['draft_picks'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to generate draft board');
    },
  });

  // Admin: Publish draft board
  const publishMutation = useMutation({
    mutationFn: async (boardId: string) => {
      const { data, error } = await supabase.rpc('publish_draft_board', {
        p_board_id: boardId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Draft board published! ${data.notifications_sent} athletes notified.`);
      queryClient.invalidateQueries({ queryKey: ['draft_board'] });
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to publish draft board');
    },
  });

  return {
    currentBoard,
    picks,
    isLoading: boardLoading || picksLoading,
    generateDraftBoard: generateMutation.mutate,
    isGenerating: generateMutation.isPending,
    publishDraftBoard: publishMutation.mutate,
    isPublishing: publishMutation.isPending,
  };
};
